/**
 * Session store (008 data-model). Holds the current customer/role/tenant,
 * tokens, activation + entitlement state. UI gates by entitlements; the server
 * remains the source of truth (006/009).
 *
 * **Persistence:** the session is persisted to localStorage so the user STAYS
 * LOGGED IN across app restarts. It is only dropped on an explicit `clear()`
 * (manual sign-out) or when a token refresh ultimately fails (`onAuthExpired`).
 *
 * **Desktop token storage (WS-Q Q-6 — the secret store is LOAD-BEARING).** On the
 * DESKTOP build the refresh token lives ONLY in the device-encrypted secret store
 * (native AES-256-GCM under the device key — `PlatformIO.secretSet/Get/Delete`);
 * `partialize` strips it out of the localStorage copy (`stripRefreshOnDesktop`),
 * so there is no longer a second copy to fall back to. `hydrateTokens()` reads it
 * at startup and patches the in-memory session; `clear()` wipes it.
 *
 * That makes a failed write or an unreadable store a real outcome — the next launch
 * has no refresh token ANYWHERE and the session ends — so neither is silent: every
 * secret-store call is still wrapped (a broken store must not take the app down with
 * it), and every swallowed failure is REPORTED through `setSessionWarn`'s hook and
 * `console.warn`. `bootstrap.ts` turns the boot-time case into a named re-authenticate
 * prompt rather than a bare "Welcome back". Token VALUES are never passed to either.
 *
 * The WEB path is byte-for-byte unchanged: both tokens in localStorage, no secret
 * store, and web never gets the long chain.
 */
import { create } from "zustand";
import { createJSONStorage, persist, type StateStorage } from "zustand/middleware";
import type { ActivationStatus, AuthTokens, Entitlements, Role, Session } from "@genz/platform";
import { currentPlatform } from "@genz/platform";

interface SessionState {
  session?: Session;
  setSession(s: Session | undefined): void;
  setTokens(t: AuthTokens): void;
  setActivation(a: ActivationStatus): void;
  setEntitlements(e: Entitlements): void;
  hasFeature(name: string): boolean;
  isAdmin(): boolean;
  isSuperAdmin(): boolean;
  role(): Role | undefined;
  clear(): void;
}

/** In-memory fallback when there's no localStorage (Node/SSR/tests without jsdom). */
function memoryStorage(): StateStorage {
  const map = new Map<string, string>();
  return {
    getItem: (k) => map.get(k) ?? null,
    setItem: (k, v) => void map.set(k, v),
    removeItem: (k) => void map.delete(k),
  };
}

/** The localStorage key of the persisted session. */
const SESSION_KEY = "genz-session";

/** Opaque secret-store key for the persisted auth tokens (desktop only). */
const TOKENS_SECRET_KEY = "genz.session.tokens";

/** A report sink for a swallowed secret-store failure. Never handed a token value. */
export type SessionWarn = (message: string, ctx?: Record<string, unknown>) => void;

let warnHook: SessionWarn | undefined;

/**
 * Register the client-observability sink for this store's swallowed failures (`bootstrap.ts`
 * wires it to `AppLogger.log` at `warn`). Without it the reports still reach `console.warn` —
 * "no silent failures" does not depend on dependency injection having happened.
 */
export function setSessionWarn(fn: SessionWarn | undefined): void {
  warnHook = fn;
}

/** Report a swallowed secret-store failure. VALUE-BLIND: a sentence and an error message. */
function warnSession(message: string, cause?: unknown): void {
  const detail = cause instanceof Error ? cause.message : cause === undefined ? "" : String(cause);
  const full = detail ? `${message} — ${detail}` : message;
  try { warnHook?.(full, { store: "session" }); } catch { /* a broken sink must not block */ }
  try { console.warn(`session: ${full}`); } catch { /* nor a broken console */ }
}

/**
 * Mirror the current session's tokens into the device-encrypted secret store (desktop only;
 * no-op on web). Best-effort so a broken store cannot take the running app down — but NOT
 * silent: on desktop this is the only place the refresh token is written, so a failure here
 * means the next launch has no refresh token at all. The token VALUES are never logged.
 */
function persistTokensSecurely(): void {
  const io = currentPlatform();
  if (!io || io.kind !== "desktop") return;
  const tokens = useSessionStore.getState().session?.tokens;
  if (!tokens) return;
  void (async () => {
    try {
      await io.secretSet(TOKENS_SECRET_KEY, JSON.stringify(tokens));
    } catch (e) {
      warnSession(
        "the sign-in could not be saved to this device's encrypted store; this machine will " +
        "ask for the password again on the next launch",
        e,
      );
    }
  })();
}

/** Best-effort delete of the tokens secret (desktop only). Swallows all errors. */
function deleteTokensSecure(): void {
  const io = currentPlatform();
  if (!io || io.kind !== "desktop") return;
  void (async () => {
    try {
      await io.secretDelete(TOKENS_SECRET_KEY);
    } catch {
      /* ignore — the in-memory + localStorage session is already cleared */
    }
  })();
}

/**
 * WS-Q Q-6 / D-35: the copy of the session that goes to localStorage, minus the refresh token
 * on desktop.
 *
 * A 15-minute access token in localStorage is a 15-minute exposure. A 180-day refresh token
 * there is a 180-day one, readable by anything that gets script into the webview — and "keep
 * me signed in on this device" is precisely what makes that token long-lived. The encrypted
 * secret store already held the full set and was already the PREFERRED source (`hydrateTokens`
 * runs before the client is handed anything); this makes it the ONLY place the refresh token
 * is written on desktop.
 *
 * WEB IS UNCHANGED, byte for byte: it has no secret store to move the token into, and web
 * never gets the long chain in the first place.
 */
function stripRefreshOnDesktop(session: Session | undefined): Session | undefined {
  const io = currentPlatform();
  if (!session || !session.tokens || !io || io.kind !== "desktop") return session;
  // The `_`-prefixed binding is the rule's own opt-out for a deliberate omission, so no
  // suppression is needed: the point of the destructure IS the discarded key.
  const { refresh_token: _keptOutOfLocalStorage, ...rest } = session.tokens;
  return { ...session, tokens: rest as AuthTokens };
}

export const useSessionStore = create<SessionState>()(
  persist(
    (set, get) => ({
      session: undefined,
      setSession: (s) => {
        set({ session: s });
        persistTokensSecurely();
      },
      setTokens: (t: AuthTokens) => {
        set((state) => (state.session ? { session: { ...state.session, tokens: t } } : {}));
        persistTokensSecurely();
      },
      setActivation: (a) =>
        set((state) => (state.session ? { session: { ...state.session, activation: a } } : {})),
      setEntitlements: (e) =>
        set((state) => (state.session ? { session: { ...state.session, entitlements: e } } : {})),
      hasFeature: (name) => {
        const f = get().session?.entitlements?.features?.[name];
        return f === true || (typeof f === "number" && f > 0);
      },
      isAdmin: () => {
        const r = get().session?.role;
        return r === "tenant_admin" || r === "super_admin";
      },
      isSuperAdmin: () => get().session?.role === "super_admin",
      role: () => get().session?.role,
      clear: () => {
        set({ session: undefined });
        // Clear BOTH stores on logout so no token survives in either place.
        deleteTokensSecure();
      },
    }),
    {
      name: SESSION_KEY,
      storage: createJSONStorage(() =>
        typeof localStorage !== "undefined" ? localStorage : memoryStorage(),
      ),
      // Persist ONLY the session (never the action functions), and on DESKTOP without the
      // refresh token — see `stripRefreshOnDesktop`. Web is byte-for-byte unchanged.
      partialize: (s) => ({ session: stripRefreshOnDesktop(s.session) }),
    },
  ),
);

/**
 * The tokens as last PERSISTED by any tab of this origin, read from storage rather than this
 * tab's memory (the client's cross-tab refresh adopts them). On desktop the copy carries no
 * refresh token, so nothing is adopted there.
 */
export function storedTokens(): AuthTokens | undefined {
  try {
    const raw = typeof localStorage !== "undefined" ? localStorage.getItem(SESSION_KEY) : null;
    return raw ? JSON.parse(raw)?.state?.session?.tokens : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Desktop bootstrap: after the synchronous localStorage rehydrate, read the tokens from the
 * device-encrypted secret store (the only place the desktop refresh token is written) and
 * patch them into the in-memory session. On web this is a no-op and the localStorage tokens
 * stand; a PRE-WS-Q blob that still carries its own refresh token is also left alone.
 *
 * Idempotent; self-invoked on module load and also exported for tests / an explicit bootstrap
 * re-run. Returns whether it patched the in-memory tokens — `false` plus a session that has
 * VANISHED is the unreadable-store outcome, which `bootstrap.ts` turns into a named prompt.
 */
export async function hydrateTokens(): Promise<boolean> {
  const io = currentPlatform();
  if (!io || io.kind !== "desktop") return false;
  let raw: string | undefined;
  let unreadable: unknown;
  try {
    raw = await io.secretGet(TOKENS_SECRET_KEY);
  } catch (e) {
    raw = undefined; // secret store unavailable → see the report below
    unreadable = e;
  }
  let tokens: AuthTokens | undefined;
  if (raw) {
    try {
      tokens = JSON.parse(raw) as AuthTokens;
    } catch (e) {
      tokens = undefined; // corrupt secret → treat as nothing stored
      unreadable = e;
    }
  }
  const cur = useSessionStore.getState().session;
  if (!cur) return false; // no restored session to attach tokens to
  if (!tokens || (!tokens.access_token && !tokens.refresh_token)) {
    // WS-Q Q-6: the refresh token is no longer written to localStorage on desktop, so when
    // the encrypted copy is missing or unreadable there is nothing left to fall back TO. End
    // the session rather than leave an identity on screen that cannot refresh and will 401 on
    // the operator's first click — a clean re-login is the honest outcome.
    //
    // A session restored from a PRE-WS-Q blob still carries its refresh token in
    // localStorage; that one is left alone, so upgrading does not sign anybody out.
    if (!cur.tokens?.refresh_token) {
      // Ending a restored session must never be quiet: a corrupt `.genz_imgkey` or a
      // permissions fault on the store is indistinguishable, to the operator, from "it logged
      // me out again". Name it here, and `bootstrap.ts` says it in the prompt.
      warnSession(
        "the saved sign-in on this device could not be read, so the session has ended and " +
        "this machine needs the password again",
        unreadable,
      );
      useSessionStore.getState().clear();
    }
    return false;
  }
  useSessionStore.setState({ session: { ...cur, tokens } });
  return true;
}

// Kick off the async token hydrate on desktop. On web/tests this resolves immediately to a
// no-op. Wrapped so a rejected promise can never break module load.
void hydrateTokens().catch(() => {});
